import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Mail, Phone, MapPin, Linkedin } from 'lucide-react';
import api from '../services/api';
import { useToast } from '../hooks/use-toast';

const ContactPage = () => {
  const [portfolioData, setPortfolioData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const fetchPortfolio = async () => {
      try {
        setLoading(true);
        const data = await api.portfolio.getAll();
        setPortfolioData(data);
      } catch (error) {
        console.error('Error fetching portfolio:', error);
        toast({
          title: 'Error',
          description: 'Failed to load portfolio data.',
          variant: 'destructive'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPortfolio();
  }, [toast]);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      toast({
        title: 'Error',
        description: 'Please fill in all fields.',
        variant: 'destructive'
      });
      return;
    }

    try {
      setSubmitting(true);
      const response = await api.contact.submit(contactForm);
      
      toast({
        title: 'Success!',
        description: response.message,
      });
      
      setContactForm({ name: '', email: '', message: '' });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to send message. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!portfolioData) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-gray-600">Failed to load data.</p>
      </div>
    );
  }

  return (
    <section className="py-20 bg-transparent">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Get In Touch</h1>
          <div className="w-20 h-1 bg-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Open to opportunities in international education</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <Card className="border-blue-100 hover:shadow-lg transition-shadow duration-300">
            <CardHeader>
              <CardTitle className="text-blue-900">Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <a href={`mailto:${portfolioData.contact.email}`} className="flex items-center gap-3 text-gray-700 hover:text-blue-600 transition-colors">
                <Mail className="w-5 h-5 text-blue-600" />
                <span>{portfolioData.contact.email}</span>
              </a>
              <div className="flex items-center gap-3 text-gray-700">
                <Phone className="w-5 h-5 text-blue-600" />
                <span>{portfolioData.contact.phone}</span>
              </div>
              <div className="flex items-center gap-3 text-gray-700">
                <MapPin className="w-5 h-5 text-blue-600" />
                <span>{portfolioData.contact.location}</span>
              </div>
              <a href={portfolioData.contact.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-gray-700 hover:text-blue-600 transition-colors">
                <Linkedin className="w-5 h-5 text-blue-600" />
                <span>Connect on LinkedIn</span>
              </a>
            </CardContent>
          </Card>

          <Card className="border-blue-100 hover:shadow-lg transition-shadow duration-300">
            <CardHeader>
              <CardTitle className="text-blue-900">Quick Message</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleContactSubmit} className="space-y-4">
                <input
                  type="text"
                  placeholder="Your Name"
                  value={contactForm.name}
                  onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                  required
                />
                <input
                  type="email"
                  placeholder="Your Email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                  required
                />
                <textarea
                  placeholder="Your Message"
                  rows="4"
                  value={contactForm.message}
                  onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                  required
                ></textarea>
                <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={submitting}>
                  {submitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default ContactPage;
